import { useEffect, useState } from 'react';
import { api, type SourcePlan } from '../api';
import { Button, Copy, Empty, ErrorNote } from './ui';

/**
 * The handoff: everything needed to get this source into Loxone Config.
 *
 * Each command recognition shown here has been run against the payload the endpoint
 * actually serves. A string we could not prove is marked, rather than presented alongside
 * the verified ones as though it were equivalent — the entire value of this panel is that
 * you can trust what you paste.
 */
export function LoxonePanel({ sourceId, refreshKey }: { sourceId: string; refreshKey: number }) {
  const [plan, setPlan] = useState<SourcePlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showGuide, setShowGuide] = useState(false);

  useEffect(() => {
    setError(null);
    void api
      .plan(sourceId)
      .then(setPlan)
      .catch((e: Error) => setError(e.message));
  }, [sourceId, refreshKey]);

  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (!plan) return <Empty>Loading…</Empty>;

  const verified = plan.commands.filter((c) => c.verified).length;
  const allVerified = verified === plan.commands.length && plan.commands.length > 0;

  return (
    <div className="space-y-4">
      {plan.warnings.length > 0 && (
        <div className="text-sm text-warn bg-warn/10 border border-warn/30 rounded-md px-3 py-2 space-y-1">
          {plan.warnings.map((w) => (
            <p key={w}>{w}</p>
          ))}
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs uppercase tracking-wide text-ink-500">1 · Import the template</span>
          <a href={api.templateUrl(sourceId)} download>
            <Button variant="primary">Download XML</Button>
          </a>
        </div>
        <p className="text-sm text-ink-300">
          In Loxone Config: <span className="text-ink-100">Virtual Inputs → Import XML template</span>. This creates
          the input and every command below in one action, with a polling cycle of {plan.pollingSeconds}s.
        </p>
      </div>

      <div>
        <div className="text-xs uppercase tracking-wide text-ink-500 mb-2">2 · Or set it up by hand</div>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-xs text-ink-500 w-28 shrink-0">Address (HTTPS)</span>
            <code className="mono text-xs text-ink-300 bg-ink-950 border border-ink-800 rounded px-2 py-1 truncate flex-1">
              {plan.urls.https}
            </code>
            <Copy text={plan.urls.https} />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-ink-500 w-28 shrink-0" title="Generation 1 Miniservers cannot fetch over TLS">
              Address (HTTP)
            </span>
            <code className="mono text-xs text-ink-300 bg-ink-950 border border-ink-800 rounded px-2 py-1 truncate flex-1">
              {plan.urls.http}
            </code>
            <Copy text={plan.urls.http} />
          </div>
          <p className="text-xs text-ink-500">
            Generation 1 Miniservers cannot fetch over TLS at all — use the HTTP address for those.
          </p>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs uppercase tracking-wide text-ink-500">
            3 · Command recognition, one per value
          </span>
          <span className={`text-xs ${allVerified ? 'text-good' : 'text-warn'}`}>
            {verified}/{plan.commands.length} verified against the live payload
          </span>
        </div>

        {plan.commands.length === 0 ? (
          <Empty>No numeric fields yet — add one and it will appear here.</Empty>
        ) : (
          <div className="border border-ink-800 rounded-md divide-y divide-ink-800">
            {plan.commands.map((c) => (
              <div key={c.key} className="flex items-center gap-2 px-3 py-1.5">
                <span
                  className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${c.verified ? 'bg-good' : 'bg-bad'}`}
                  title={c.verified ? 'proven against the live payload' : (c.error ?? 'could not be verified')}
                />
                <span className="text-xs text-ink-300 w-40 truncate shrink-0">{c.title}</span>
                <code className="mono text-xs text-accent bg-ink-950 border border-ink-800 rounded px-2 py-0.5 flex-1 truncate">
                  {c.check}
                </code>
                <span className="mono text-xs text-ink-500 w-20 text-right truncate" title="value extracted just now">
                  {c.extracted ?? '—'}
                </span>
                {c.lenient && (
                  <span className="text-xs text-warn" title="matched only with whitespace tolerance the Miniserver may not share">
                    lenient
                  </span>
                )}
                <Copy text={c.check} />
              </div>
            ))}
          </div>
        )}
      </div>

      {plan.unsupported.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wide text-ink-500 mb-2">Not pollable by a Virtual HTTP Input</div>
          <div className="border border-ink-800 rounded-md divide-y divide-ink-800">
            {plan.unsupported.map((u) => (
              <div key={u.key} className="px-3 py-2">
                <span className="mono text-xs text-ink-300">{u.key}</span>
                <p className="text-xs text-ink-500 mt-0.5">{u.reason}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <button onClick={() => setShowGuide(!showGuide)} className="text-xs text-accent hover:underline">
          {showGuide ? 'Hide' : 'Show'} step-by-step guide
        </button>
        {showGuide && (
          <ol className="mt-2 space-y-2 text-sm text-ink-300 list-decimal list-inside">
            <li>Open Loxone Config and connect to the Miniserver.</li>
            <li>
              In the periphery tree, select <span className="text-ink-100">Virtual Inputs</span>.
            </li>
            <li>
              In the ribbon choose <span className="text-ink-100">Import XML template</span> and pick the downloaded
              file.
            </li>
            <li>
              Confirm the address matches one above. Use the HTTP address if this is a Generation 1 Miniserver.
            </li>
            <li>
              Save to the Miniserver. Each command should show the value listed above within one polling cycle.
            </li>
            <li>
              If a value stays at 0, check the input received anything at all: a Gen1 Miniserver pointed at an HTTPS
              address gets an empty body and no error.
            </li>
          </ol>
        )}
      </div>

      <details className="text-xs">
        <summary className="cursor-pointer text-ink-500 hover:text-ink-300">
          What the Miniserver actually receives
        </summary>
        <pre className="mono mt-2 bg-ink-950 border border-ink-800 rounded-md p-3 overflow-auto max-h-56 text-ink-300">
          {plan.payload || '(empty — no fields yet)'}
        </pre>
      </details>
    </div>
  );
}
